import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu, Spin, Alert, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import menuConfigService from '../../services/menuConfigService';
import type { UserMenu } from '../../types/menu';

interface DynamicSidebarProps {
  width?: number;
}

const DynamicSidebar: React.FC<DynamicSidebarProps> = ({ width = 300 }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [menuTree, setMenuTree] = useState<UserMenu[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 加载用户菜单
  const loadUserMenus = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔄 [DynamicSidebar] 开始加载动态菜单...');
      console.log('👤 [DynamicSidebar] 当前用户:', user?.username, '角色:', user?.role);

      // 仅使用新的菜单配置服务获取用户菜单（与旧系统完全解耦）
      const cfgMenus = await menuConfigService.getUserMenus() as any;
      console.log('📦 [DynamicSidebar] menu-config 返回条数:', Array.isArray(cfgMenus) ? cfgMenus.length : '非数组');
      const menus: UserMenu[] = Array.isArray(cfgMenus) ? cfgMenus : [];
      console.log('📊 [DynamicSidebar] 最终菜单条数:', menus.length);
      if (menus.length === 0) {
        console.warn('🚫 [DynamicSidebar] 动态菜单为空：可能的原因：1) 该细分角色未配置可见菜单 2) 顶级菜单未勾选 3) 接口鉴权/返回异常');
      }

      setMenuTree(menus);
    } catch (error) {
      console.error('❌ [DynamicSidebar] 加载动态菜单失败:', error);
      setError('加载菜单失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadUserMenus();
    }
  }, [user]);

  // 转换菜单数据为 Ant Design Menu 格式
  const convertMenuItems = (items: UserMenu[]): MenuProps['items'] => {
    const lang = (i18n?.language || '').toLowerCase();
    const isEnglish = lang.startsWith('en');
    const isChinese = lang.startsWith('zh');
    console.log('[DynamicSidebar][i18n] language =', lang, 'isEnglish=', isEnglish, 'isChinese=', isChinese);
    return items.map(item => {
      const hasChildren = Array.isArray(item.children) && item.children.length > 0;
      // 处理图标
      let iconElement: React.ReactNode = null;
      if (item.icon) {
        try {
          // 动态导入图标组件
          const iconModule = require('@ant-design/icons');
          const IconComponent = iconModule[item.icon];
          if (IconComponent) {
            iconElement = React.createElement(IconComponent, { style: { fontSize: 16, lineHeight: 1, display: 'inline-flex', alignItems: 'center' } });
          }
        } catch (error) {
          console.warn(`[DynamicSidebar] 无法加载图标: ${item.icon}`, error);
        }
      }

      const nameEn = (item as any).name_en as string | undefined;
      // 严格：仅按 name/name_en 显示，不使用 i18n_key，不做回退
      const labelText = isEnglish ? (nameEn as any) : item.name;
      console.log('[DynamicSidebar][label]', {
        id: (item as any).id,
        lang,
        name: item.name,
        name_en: nameEn,
        resolved: labelText
      });
      return {
        key: item.path || `menu-${item.id}`,
        icon: iconElement,
        label: labelText,
        children: hasChildren ? convertMenuItems(item.children as UserMenu[]) : undefined,
        onClick: hasChildren ? undefined : () => {
          if (item.path) {
            navigate(item.path);
          }
        },
      };
    });
  };

  const menuItemsRaw = useMemo(() => convertMenuItems(menuTree), [menuTree, i18n?.language]);
  const wrapMenuItems = (items: any[] | undefined): any[] | undefined => {
    if (!Array.isArray(items)) return items;
    return items.map((it: any) => {
      if (!it) return it;
      const label = it.label;
      const wrapped = (
        <Tooltip placement="right" title={typeof label === 'string' ? label : undefined}>
          <span style={{ display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>{label}</span>
        </Tooltip>
      );
      return { ...it, label: wrapped, children: wrapMenuItems(it.children) };
    });
  };
  const menuItems = useMemo(() => wrapMenuItems(menuItemsRaw), [menuItemsRaw]);

  // 获取当前选中的菜单项
  const getSelectedKeys = () => {
    const currentPath = location.pathname;
    const findSelectedKey = (items: UserMenu[]): string[] => {
      for (const item of items) {
        const itemPath = item.path || `menu-${item.id}`;
        if (itemPath === currentPath) {
          return [itemPath];
        }
        if (item.children) {
          const childResult = findSelectedKey(item.children);
          if (childResult.length > 0) {
            return [itemPath, ...childResult];
          }
        }
      }
      return [];
    };
    return findSelectedKey(menuTree);
  };

  // 获取当前展开的子菜单
  const getOpenKeys = () => {
    const currentPath = location.pathname;
    const findOpenKeys = (items: UserMenu[]): string[] => {
      for (const item of items) {
        if (item.children) {
          for (const child of item.children) {
            const childPath = child.path || `menu-${child.id}`;
            if (childPath === currentPath || currentPath.startsWith(childPath)) {
              const itemPath = item.path || `menu-${item.id}`;
              return [itemPath];
            }
          }
          const childResult = findOpenKeys(item.children);
          if (childResult.length > 0) {
            const itemPath = item.path || `menu-${item.id}`;
            return [itemPath, ...childResult];
          }
        }
      }
      return [];
    };
    return findOpenKeys(menuTree);
  };

  if (loading) {
    return (
      <div className="bg-white shadow-lg flex items-center justify-center" style={{ width }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white shadow-lg" style={{ width }}>
        <Alert
          message="菜单加载失败"
          description={error}
          type="error"
          showIcon
          action={
            <button onClick={loadUserMenus} className="text-blue-600 hover:text-blue-800">重试</button>
          }
        />
      </div>
    );
  }

  return (
    <div className="bg-white shadow-lg" style={{ width }}>
      <div className="flex-1 overflow-y-auto">
        {menuTree.length > 0 ? (
          <Menu
            mode="inline"
            selectedKeys={getSelectedKeys()}
            defaultOpenKeys={getOpenKeys()}
            items={menuItems}
            className="border-0"
            style={{ height: 'calc(100vh - 64px)' }}
          />
        ) : (
          <div className="p-4 text-center text-gray-500">
            <div>暂无可用菜单</div>
            <div className="text-xs mt-1">请联系管理员在“角色可见性”中勾选该细分角色的顶级菜单</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DynamicSidebar;
